import React, { useEffect, useState, useMemo } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Plus, CalendarPlus, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  TRIAL: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
  PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  EXPIRED: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
  SUSPENDED: "bg-red-200 text-red-800 dark:bg-red-500/30 dark:text-red-200",
  CANCELLED: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

export default function SuperAdminSubscriptions() {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [openExtend, setOpenExtend] = useState(null);
  const [form, setForm] = useState({ company_id: "", plan_id: "basic", billing_cycle: "MONTHLY", auto_renew: false });
  const [extendDays, setExtendDays] = useState(30);

  const load = async () => {
    try {
      const [subs, pls, cos] = await Promise.all([
        api.get("/superadmin/subscriptions"),
        api.get("/superadmin/plans"),
        api.get("/superadmin/companies"),
      ]);
      setItems(subs.data); setPlans(pls.data); setCompanies(cos.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((s) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (q && ![s.company_name, s.plan_name].filter(Boolean).some((v) => v.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }), [items, statusFilter, q]);

  const create = async () => {
    if (!form.company_id) { toast.error("Sélectionnez une entreprise"); return; }
    try {
      await api.post("/superadmin/subscriptions", { ...form, start_immediately: true });
      toast.success("Abonnement créé"); setOpenNew(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const extend = async () => {
    try {
      await api.post(`/superadmin/subscriptions/${openExtend.id}/extend`, { days: Number(extendDays) });
      toast.success(`Prolongé de ${extendDays} jours`); setOpenExtend(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancel = async (s) => {
    if (!window.confirm(`Annuler l'abonnement de "${s.company_name}" ?`)) return;
    try {
      await api.post(`/superadmin/subscriptions/${s.id}/cancel`);
      toast.success("Annulé"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Abonnements</h1>
          <p className="text-sm text-slate-500">{items.length} abonnements enregistrés · Actifs : {items.filter(s => s.status === "ACTIVE").length}</p>
        </div>
        <Button className="btn-pill bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setOpenNew(true)} data-testid="sa-sub-new-btn"><Plus className="w-4 h-4 mr-1" /> Nouvel abonnement</Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Rechercher entreprise ou plan…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="sa-sub-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-48" data-testid="sa-sub-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {["ACTIVE", "TRIAL", "PENDING", "EXPIRED", "SUSPENDED", "CANCELLED"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="text-left p-3">Entreprise</th>
                <th className="text-left">Plan</th>
                <th className="text-left">Cycle</th>
                <th className="text-right">Montant</th>
                <th className="text-left">Statut</th>
                <th className="text-left">Expiration</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {filtered.map((s) => {
                const cls = STATUS_STYLE[s.status] || STATUS_STYLE.PENDING;
                const exp = s.expiration_date ? new Date(s.expiration_date) : null;
                const active = s.status === "ACTIVE" || s.status === "TRIAL";
                return (
                  <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/50" data-testid={`sa-sub-row-${s.id}`}>
                    <td className="p-3"><div className="font-semibold">{s.company_name || "—"}</div><div className="text-xs text-slate-500 font-mono">{s.company_id.slice(0, 8)}…</div></td>
                    <td className="font-semibold">{s.plan_name}</td>
                    <td className="text-xs">{s.billing_cycle}</td>
                    <td className="text-right font-mono">{formatCurrency(s.amount, s.currency)}</td>
                    <td><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cls}`}>{s.status}</span></td>
                    <td className="text-xs">{exp ? exp.toLocaleDateString("fr-FR") : "—"}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {active && (
                        <Button variant="ghost" size="sm" onClick={() => { setOpenExtend(s); setExtendDays(30); }} data-testid={`sa-sub-extend-${s.id}`}><CalendarPlus className="w-4 h-4 mr-1" /> Prolonger</Button>
                      )}
                      {s.status !== "CANCELLED" && (
                        <Button variant="ghost" size="sm" onClick={() => cancel(s)} data-testid={`sa-sub-cancel-${s.id}`}><XCircle className="w-4 h-4 mr-1" /> Annuler</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Aucun abonnement</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* New subscription dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="sa-sub-new-dialog">
          <DialogHeader><DialogTitle>Nouvel abonnement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Entreprise</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="sa-sub-new-company"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan</Label>
              <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                <SelectTrigger data-testid="sa-sub-new-plan"><SelectValue /></SelectTrigger>
                <SelectContent>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — {formatCurrency(p.price_monthly, p.currency)}/mo</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                <SelectTrigger data-testid="sa-sub-new-cycle"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MONTHLY">Mensuel</SelectItem><SelectItem value="YEARLY">Annuel</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Annuler</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={create} data-testid="sa-sub-new-submit">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend dialog */}
      <Dialog open={!!openExtend} onOpenChange={(o) => !o && setOpenExtend(null)}>
        <DialogContent data-testid="sa-sub-extend-dialog">
          <DialogHeader><DialogTitle>Prolonger l'abonnement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-500">{openExtend?.company_name} · {openExtend?.plan_name}</div>
            <div>
              <Label>Nombre de jours</Label>
              <Input type="number" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} data-testid="sa-sub-extend-days" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenExtend(null)}>Annuler</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={extend} data-testid="sa-sub-extend-submit"><RefreshCw className="w-4 h-4 mr-1" /> Prolonger</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
